import React, { useState } from 'react';
import _ from 'lodash';

import FormValidator from '../lib/FormValidator';
import { updateNestedObjectValue } from '../lib/UtilityHelper';

const DirtyModal = React.lazy(() => import('../components/Modals/DirtyModal/DirtyModal'));

const withForm = WrappedComponent => props => {
    const [showDirtyModal, setShowDirtyModal] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [validations, setValidations] = useState();
    const [form, setForm] = useState({});

    const changedHandler = (id, value, setDirty = true) => {
        setIsDirty(setDirty);
        setIsValidated(false);

        setForm(prevForm => {
            const newForm = { ...prevForm };

            if (id.includes('.')) {
                updateNestedObjectValue(newForm, id, value);
            } else {
                newForm[id] = value;
            }

            return newForm;
        });
    };

    const cancelledHandler = (options) => {
        const showModal = (_.isEmpty(options) || !options.ignoreDirty);

        if (isDirty && showModal) {
            setShowDirtyModal(true);
        } else {
            setIsDirty(false);
            setForm({});
            setIsClosing(true);
        }
    };

    const submittedHandler = () => {
        setIsDirty(false);
        setShowDirtyModal(false);
    };

    const validateHandler = (validationRules) => {
        setValidations(null);
        setIsValidated(false);
        
        const validations = new FormValidator(validationRules).validate(form);

        setValidations(validations);
        setIsValidated(validations.isValid);

        return validations.isValid;
    };

    const validationCheckHandler = (id) => {
        if (!_.isEmpty(validations) && !validations.isInvalid) {
            if (validations[id] && validations[id].isInvalid) {
                return validations[id].message;
            }
        }

        return null;
    };

    const validationResetHandler = () => {
        setValidations(null);
        setIsValidated(false);
    };

    const validationClearPrefixHandler = (prefix) => {
        setValidations((current) => {
            if (!current) return current;

            const fields = Object.entries(current).filter(
                ([key]) => key !== 'isValid' && !key.startsWith(prefix)
            );

            return {
                isValid: fields.every(([, result]) => !result.isInvalid),
                ...Object.fromEntries(fields)
            };
        });
        setIsValidated(false);
    };

    const initializeHandler = (data) => {
        setShowDirtyModal(false);
        setIsValidated(false);
        setIsClosing(false);
        setValidations(null);

        !_.isEmpty(data) ? setForm(data) : setForm({});
    };

    const noDirtyClicked = () => {
        setShowDirtyModal(false);
    };

    const yesDirtyClicked = () => {
        setShowDirtyModal(false);
        setIsClosing(true);
        setIsDirty(false);
        setForm({});

    };

    const setDirtyHandler = () => {
        setIsDirty(true);
        setIsValidated(false);
    };

    const setCleanHandler = () => {
        setIsDirty(false);
        setIsValidated(false);
    };

    return (
        <React.Fragment>
            <DirtyModal show={showDirtyModal}
                noClick={noDirtyClicked}
                yesClick={yesDirtyClicked} />

            <WrappedComponent {...props}
                isValidated={isValidated}
                validations={validations}
                formInitialize={initializeHandler}
                formData={form}
                formDirty={isDirty}
                formSetDirty={setDirtyHandler}
                formSetClean={setCleanHandler}
                formClosing={isClosing}
                formChanged={changedHandler}
                formCancelled={cancelledHandler}
                formSubmitted={submittedHandler}
                formValidate={validateHandler}
                formValidationCheck={validationCheckHandler}
                formValidationReset={validationResetHandler}
                formValidationClearPrefix={validationClearPrefixHandler} />
        </React.Fragment>
    );
}

export default withForm;
